// The span the Cycles list prints under each cycle's id.
//
// Its own module rather than a helper inside CyclesTab because
// `react-refresh/only-export-components` refuses a non-component export from a
// module that renders one — the rule this project's ESLint config adopted with
// "formatters, column specs and hooks moved into sibling modules next to their
// page" — and because a pure formatter is worth testing directly rather than
// through a render.

import type { Cycle } from "../types";

/** A day of the year: "Sep 1". */
const DAY_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
});

/**
 * A day and the time on it: "Sep 1, 14:00".
 *
 * The reader's own zone and a 24-hour clock, matching ABS_FMT in CyclesTab —
 * the list and the panel beside it are two halves of one screen, and a cycle
 * that opened at 14:00 in one of them cannot open at 2 PM or at 11:00 in the
 * other.
 */
const DAY_TIME_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

/** The time alone: "14:20". */
const TIME_FMT = new Intl.DateTimeFormat("en-US", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * A cycle's span, with the clock on it only where the clock says something.
 *
 * Under a day, the time is the ONLY thing that tells one cycle from the next:
 * a board on 20-minute windows fills the column with identical "Sep 1 – Sep 1"
 * rows, and the id beside them is a bare UUID. A day or longer keeps the dates
 * alone — a weekly board opens at 00:00, and printing that would hang a
 * meaningless number off every date on every board that exists today.
 *
 * Exactly 24 hours counts as long: a daily board already reads "Sep 1 – Sep 2",
 * and that is right.
 *
 * The second mark carries only what differs from the first — inside one day
 * that is the time, across midnight the whole date.
 */
export function fmtCycleRange(c: Cycle): string {
  const start = new Date(c.open_at);
  if (Number.isNaN(start.getTime())) return "—";
  const end = c.close_at ? new Date(c.close_at) : null;
  // A cycle with no close is an all_time board's single everlasting one, and
  // only that: FirstCycleFor leaves CloseAt nil on that kind alone
  // (snapshot_ops.go), planSuccessor always sets it, and no write ever nulls
  // it. So the length here is unknown rather than long — and the one mark
  // there is gets printed as precisely as it is known. A recurring board's
  // OPEN cycle does not come through here; it carries the close its worker
  // scheduled, which is why the short board's open row still reads as a span.
  if (!end || Number.isNaN(end.getTime())) return DAY_TIME_FMT.format(start);
  if (end.getTime() - start.getTime() >= DAY_MS) {
    return `${DAY_FMT.format(start)} – ${DAY_FMT.format(end)}`;
  }
  const tail = sameDay(start, end)
    ? TIME_FMT.format(end)
    : DAY_TIME_FMT.format(end);
  return `${DAY_TIME_FMT.format(start)} – ${tail}`;
}

/** In the reader's own zone, which is the zone both marks are printed in. */
function sameDay(a: Date, b: Date): boolean {
  return a.toDateString() === b.toDateString();
}
